// First visit after connecting Google: one screen to confirm the spa's details, then go.

import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { api } from '../lib/api';
import { navigate } from '../lib/router';
import { Button, Field, Spinner, toastError } from '../lib/ui';
import { Butterfly } from '../parts/Butterfly';
import { loadSettings, setSettingsBundle, type SettingsBundle } from '../lib/store';

export function Welcome() {
  const [s, setS] = useState<SettingsBundle | null>(null);
  const [account, setAccount] = useState<string | null>(null);
  const [form, setForm] = useState({ from_name: '', address: '', phone: '', test: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([loadSettings(true), api<{ account: string | null }>('/api/google/status')])
      .then(([b, g]) => {
        setS(b);
        setAccount(g.account);
        setForm({
          from_name: b.settings.from_name,
          address: b.settings.address,
          phone: b.settings.phone,
          test: b.settings.test_emails[0] || g.account || b.settings.from_email,
        });
      })
      .catch(toastError);
  }, []);

  const set = (k: keyof typeof form) => (e: Event) => setForm({ ...form, [k]: (e.currentTarget as HTMLInputElement).value });

  // The address box grows to fit, so the whole address (and the ZIP) shows on a phone.
  const addr = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = addr.current;
    if (!el) return;
    const fit = () => {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight + 2}px`;
    };
    fit();
    addEventListener('resize', fit);
    return () => removeEventListener('resize', fit);
  }, [form.address, s]);

  const go = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    try {
      const out = await api<SettingsBundle>('/api/settings', {
        method: 'PUT',
        body: { from_name: form.from_name, address: form.address, phone: form.phone, test_emails: [form.test].filter(Boolean), onboarded: true },
      });
      setSettingsBundle(out);
      navigate('/', { replace: true });
    } catch (err) {
      toastError(err);
      setBusy(false);
    }
  };

  if (!s) return <div class="auth"><Spinner /></div>;
  const sendingFrom = account || s.settings.from_email;

  return (
    <div class="auth">
      <form class="auth-card welcome" onSubmit={go}>
        <div class="welcome-check">
          <Butterfly size={36} dark open />
        </div>
        <h1 class="display">You're all set, Jen</h1>
        <p class="lead welcome-lead">
          Emails go out from <b>{sendingFrom}</b>. The details below go at the bottom of every email.
        </p>
        <Field label="Spa name">
          <input value={form.from_name} onInput={set('from_name')} required />
        </Field>
        <Field label="Address" hint="Required by law.">
          <textarea
            ref={addr}
            class="welcome-address"
            rows={1}
            value={form.address}
            onInput={set('address')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.form?.requestSubmit();
              }
            }}
            required
          />
        </Field>
        <Field label="Phone" hint="Clients can tap it to call you.">
          <input value={form.phone} onInput={set('phone')} />
        </Field>
        <Field label="Send test emails to" hint="You get a test copy before every send.">
          <input type="email" value={form.test} onInput={set('test')} required />
        </Field>
        <Button kind="primary" size="l" type="submit" busy={busy} class="wide" icon="next">
          Looks good, let's go
        </Button>
      </form>
    </div>
  );
}
